import { toast } from "sonner";
import { markdownLink } from "@brainlog/shared";
import type { FileRow } from "./types";
import { LockedError, fetchDecryptedFile, saveBlob } from "@/features/crypto/ops";

type FileLike = Pick<FileRow, "id" | "encrypted" | "filename"> & Partial<Pick<FileRow, "mime" | "encMeta">>;

export const LOCKED_FILE_MESSAGE = "Unlock this note or project to open the file.";

/** Decrypt an end-to-end encrypted file in the browser (null and a message when it is locked). */
export async function decryptedFile(file: FileLike) {
  if (!file.encMeta) {
    toast.error("This file's encryption data is missing.");
    return null;
  }
  try {
    return await fetchDecryptedFile({ id: file.id, encMeta: file.encMeta });
  } catch (err) {
    toast.error(err instanceof LockedError ? LOCKED_FILE_MESSAGE : (err as Error).message);
    return null;
  }
}

/** Server URL of the stored bytes; null for end-to-end encrypted files (they must be decrypted in the browser). */
export function fileUrl(file: FileLike, opts: { download?: boolean } = {}): string | null {
  if (file.encrypted) return null;
  return `/api/attachments/${file.id}/file${opts.download ? "?download=1" : ""}`;
}

/** Save the file to disk. */
export async function downloadFile(file: FileLike) {
  if (file.encrypted) {
    const f = await decryptedFile(file);
    if (f) saveBlob(f.blob, f.meta.filename);
    return;
  }
  const url = fileUrl(file, { download: true });
  if (!url) {
    toast.error(LOCKED_FILE_MESSAGE);
    return;
  }
  const a = document.createElement("a");
  a.href = url;
  a.download = file.filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Open PDFs and images in a new browser tab; everything else is downloaded. */
export async function openFile(file: FileLike) {
  if (file.encrypted) {
    const f = await decryptedFile(file);
    if (!f) return;
    if (f.meta.mime === "application/pdf" || /^image\/(png|jpeg|gif|webp)$/.test(f.meta.mime)) {
      // The decrypted copy exists only in this tab's memory.
      const url = URL.createObjectURL(f.blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } else saveBlob(f.blob, f.meta.filename);
    return;
  }
  const inline = !!file.mime && (file.mime === "application/pdf" || /^image\/(png|jpeg|gif|webp)$/.test(file.mime));
  if (!inline) return downloadFile(file);
  const url = fileUrl(file);
  if (!url) {
    toast.error(LOCKED_FILE_MESSAGE);
    return;
  }
  window.open(url, "_blank", "noopener");
}

/** Copy a stable Markdown link (`[name](bl://file/<id>)`) to paste into any note. */
export async function copyFileLink(file: FileLike) {
  const md = markdownLink(file.encrypted ? "Encrypted file" : file.filename, { type: "file", id: file.id });
  try {
    await navigator.clipboard.writeText(md);
    toast.success("Link copied. Paste it into any note in this notebook or project.");
  } catch {
    toast.message(md, { description: "Copy this link manually." });
  }
}
